import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserOrganization } from "./use-user-organization";

interface OrganizationDevice {
  device_id: string;
  device_name: string;
  device_description?: string;
}

export const useOrganizationDevices = () => {
  const { data: organizationId } = useUserOrganization();

  return useQuery({
    queryKey: ['organization-devices', organizationId],
    queryFn: async (): Promise<OrganizationDevice[]> => {
      if (!organizationId) return [];

      // Get all users in the organization
      const { data: orgUsers, error: usersError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('organization_id', organizationId);

      if (usersError) {
        console.error('Error fetching organization users:', usersError);
        throw usersError;
      }

      if (!orgUsers || orgUsers.length === 0) {
        return [];
      }

      const userIds = orgUsers.map(u => u.user_id);

      // Get device assignments for all organization users
      const { data: assignments, error: assignmentsError } = await supabase
        .from('device_assignments')
        .select('device_id')
        .in('user_id', userIds);

      if (assignmentsError) {
        console.error('Error fetching device assignments:', assignmentsError);
        throw assignmentsError;
      }

      if (!assignments || assignments.length === 0) {
        return [];
      }

      const deviceIds = [...new Set(assignments.map(a => a.device_id))]; // Remove duplicates

      // Get device details
      const { data: devices, error: devicesError } = await supabase
        .from('device_details')
        .select('device_id, device_name, device_description')
        .in('device_id', deviceIds)
        .order('device_name');

      if (devicesError) {
        console.error('Error fetching device details:', devicesError);
        throw devicesError;
      }

      return devices || [];
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};